/**
 * `WebSocketLike` — the minimal, DOM-free WebSocket abstraction the shared
 * `JsonRpcConnection` core programs against. The single real environment seam
 * between the browser `WebSocket` and node `ws` (P0b of
 * `docs/cloud-vm/design/crucible-client-sdk-tdd.md` §3.1).
 *
 * It is deliberately tiny — send / close / readyState + four event callbacks —
 * so the core never imports the DOM `WebSocket` type and stays node-consumable
 * (the node CLI consumes the core + node transport with NO DOM lib; only the
 * browser transport pulls DOM types).
 *
 * The browser's `addEventListener` + `MessageEvent`/`CloseEvent` model and node
 * `ws`'s `EventEmitter` + `Buffer` model are each adapted to this normalised
 * callback shape INSIDE their respective transport (see `./transports/`), not
 * in the core. That adaptation is the whole reason the two wire clients can't
 * share code today — and the seam that fixes it.
 */

/** WebSocket `readyState` values (browser + `ws` agree on these numerics). */
export const WS_CONNECTING = 0;
export const WS_OPEN = 1;
export const WS_CLOSING = 2;
export const WS_CLOSED = 3;

/**
 * The normalised socket the core drives. A transport adapter implements this
 * over its environment's native socket.
 *
 * Contract notes:
 *   - `send` is only called by the core when `readyState === WS_OPEN`; an
 *     adapter MAY still throw (e.g. a socket that closed between the guard and
 *     the call) — the core treats a throw as a transport-unavailable error.
 *   - the four `on*` callbacks are assigned by the core after construction.
 *     An adapter MUST invoke them asynchronously (never synchronously from
 *     inside `send`/`close`) so the core's state machine isn't re-entered
 *     mid-call. `onmessage` always receives a decoded UTF-8 `string` (the
 *     node adapter coerces `Buffer`; both clients speak text JSON-RPC frames).
 *   - `onclose` carries the close `code` + `reason`; the core maps 4401/4402
 *     to auth failures and other non-1000 codes to retryable network closes,
 *     matching the existing `BrowserCrucibleClient` close handling.
 */
export interface WebSocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  readonly readyState: number;
  onopen: (() => void) | null;
  onmessage: ((data: string) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: (() => void) | null;
}

/**
 * How a client attaches identity to the WSS upgrade. The transport translates
 * it to the environment-appropriate mechanism; the core stays auth-agnostic
 * (TDD §3.4). Every kind resolves to the same `ownerSub` server-side
 * (bifrost#109 / Crucible #1274).
 *
 *   - `cookie` — same-origin browser deployment; the browser attaches the
 *     HttpOnly ALB session cookie on the upgrade automatically. No protocols,
 *     no headers. The ONLY product auth path for the browser SPA today.
 *   - `subprotocol-jwt` — browser PKCE deployments: the JWT rides as the
 *     `crucible-auth.<jwt>` WebSocket subprotocol (the only Authorization
 *     equivalent JS can set on a browser upgrade).
 *   - `header-bearer` — node/CLI/mobile/TV device-flow: `Authorization:
 *     Bearer <token>` on the upgrade (node + RN can set upgrade headers).
 *   - `stub` — dev/smoke only: the `X-Crucible-Auth-Stub-User-Id`
 *     (+ optional `X-Crucible-Auth-Stub-Scope`) header the existing runtime
 *     stub-auth path reads (`packages/crucible/src/auth/stub.ts`,
 *     `runtime-serve.ts`). NOT a product path.
 *   - `headers` — node-only escape hatch for arbitrary upgrade headers; the
 *     in-pod smoke harness uses it to inject `X-Amzn-Oidc-Identity` directly
 *     because `port-forward` is RBAC-blocked
 *     (`scripts/forge-smoke/cookie-mode-smoke.ts`). Browsers cannot set
 *     upgrade headers, so the browser transport rejects this kind.
 */
export type AuthAttachment =
  | { kind: 'cookie' }
  | { kind: 'subprotocol-jwt'; token: string }
  | { kind: 'header-bearer'; token: string }
  | { kind: 'stub'; userId: string; scope?: string }
  | { kind: 'headers'; headers: Record<string, string> };

/**
 * A per-connect credential resolver (D1, §4.1 of the P3 SDK design + its TDD
 * `crucible-client-sdk-d1-authsource-tdd.md`). `JsonRpcConnectionOptions.auth`
 * accepts either a fixed {@link AuthAttachment} or this thunk; `connect()` calls
 * the thunk **synchronously** immediately before opening the transport, so a
 * rotated/refreshed credential reaches the upgrade on every connect AND reconnect
 * with NO pre-socket await — there is no supersede window a concurrent `connect()`
 * could race (§10 Q1, resolved sync). Any async refresh is the policy/caller's job,
 * done BEFORE it triggers `connect()` (the policy resolves into a field the thunk reads).
 */
export type AuthResolver = () => AuthAttachment;

/**
 * Opens a `WebSocketLike` against `url` with the given auth attachment. Each
 * environment supplies its own factory:
 *   - `browserWebSocketTransport` (DOM `WebSocket`; cookie / subprotocol-jwt)
 *   - `nodeWebSocketTransport` (`ws`; header-bearer / stub / headers)
 *
 * A factory MUST throw a typed error for an `AuthAttachment` kind it cannot
 * honour (e.g. the browser transport given `header-bearer`) rather than
 * silently dropping the credential.
 */
export type WebSocketTransportFactory = (
  url: string,
  auth: AuthAttachment,
) => WebSocketLike;
